import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, Search, Plus, Download, Trash2, Wallet, TrendingDown, Receipt, HandCoins, Truck } from "lucide-react";
import { PayStatusBadge } from "@/components/PayStatusBadge";
import { toast } from "sonner";
import { ExpenseCategoryField } from "@/components/ExpenseCategoryField";

const MODES = ["Bank Transfer", "UPI", "Cash", "Cheque", "Credit / Debit Card", "Other"];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const SummaryCard = ({ icon: Icon, label, value, accent }) => (
  <div className="border border-border p-4 bg-card" data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground"><Icon className={`h-3.5 w-3.5 ${accent || ""}`} />{label}</div>
    <div className={`font-mono-num text-2xl font-bold mt-1.5 ${accent || ""}`}>{money(value)}</div>
  </div>
);

export default function VendorPaymentsPage() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = () => api.get("/vendor-payments").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => [r.order_number, r.vendor_name, r.customer_name, r.route].some((v) => String(v || "").toLowerCase().includes(q)))
    : rows;

  const totals = filtered.reduce((a, r) => ({
    order_value: a.order_value + r.order_value, paid: a.paid + r.paid, pending: a.pending + r.pending, misc: a.misc + r.misc,
  }), { order_value: 0, paid: 0, pending: 0, misc: 0 });

  return (
    <div className="p-6 space-y-6" data-testid="vendor-payments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-2"><Banknote className="h-7 w-7 text-primary" /> Vendor Payments</h1>
          <p className="text-muted-foreground text-sm">Track amounts payable to transporters on confirmed orders, record payments &amp; misc. expenses, and generate vouchers.</p>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="rounded-none pl-9 w-72 bg-card" placeholder="Search order, vendor, customer…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="vp-search" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={Wallet} label="Total Order Value" value={totals.order_value} />
        <SummaryCard icon={HandCoins} label="Total Paid" value={totals.paid} accent="text-emerald-500" />
        <SummaryCard icon={TrendingDown} label="Total Pending" value={totals.pending} accent="text-primary" />
        <SummaryCard icon={Receipt} label="Total Misc. Expenses" value={totals.misc} />
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="vp-table">
          <thead className="bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Order</th>
              <th className="px-3 text-left">Vendor</th>
              <th className="px-3 text-left">Order Date</th>
              <th className="px-3 text-left">Route</th>
              <th className="px-3 text-right">Order Value</th>
              <th className="px-3 text-right">Misc.</th>
              <th className="px-3 text-right">Paid</th>
              <th className="px-3 text-right">Pending</th>
              <th className="px-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No confirmed orders with an assigned vendor yet.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`vp-row-${r.order_number}`}>
                <td className="px-3 py-2 font-mono-num font-bold text-primary">{r.order_number}<div className="text-[11px] text-muted-foreground font-normal">{r.customer_name}</div></td>
                <td className="px-3">{r.vendor_name}</td>
                <td className="px-3 text-xs text-muted-foreground whitespace-nowrap">{r.date ? new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</td>
                <td className="px-3 text-xs text-muted-foreground">{r.route}</td>
                <td className="px-3 text-right font-mono-num">{money(r.order_value)}</td>
                <td className="px-3 text-right font-mono-num text-muted-foreground">{money(r.misc)}</td>
                <td className="px-3 text-right font-mono-num text-emerald-500">{money(r.paid)}</td>
                <td className={`px-3 text-right font-mono-num font-bold ${r.pending > 0 ? "text-primary" : "text-emerald-500"}`}>{money(r.pending)}</td>
                <td className="px-3 text-right">
                  <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={() => setActiveId(r.id)} data-testid={`vp-manage-${r.order_number}`}>Manage</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeId && <ManageDialog leadId={activeId} onClose={() => setActiveId(null)} onChanged={load} />}
    </div>
  );
}

function ManageDialog({ leadId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [pay, setPay] = useState({ amount: "", mode: "Bank Transfer", mode_other: "", note: "", date: "", lr_id: "" });
  const [exp, setExp] = useState({ category: "", vendor_name: "", amount: "", remarks: "" });
  const [expKey, setExpKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/leads/${leadId}/vendor-payments`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const addPayment = async () => {
    const amt = parseFloat(pay.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    if (pay.mode === "Other" && !pay.mode_other.trim()) return toast.error("Specify the payment mode");
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/vendor-payments`, { amount: amt, mode: pay.mode, mode_other: pay.mode_other.trim(), note: pay.note.trim(), date: pay.date, lr_id: pay.lr_id });
      setD(data); setPay({ amount: "", mode: "Bank Transfer", mode_other: "", note: "", date: "", lr_id: "" });
      toast.success("Payment recorded"); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const addExpense = async () => {
    const amt = parseFloat(exp.amount);
    if (!exp.category.trim()) return toast.error("Enter a category");
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/expenses`, { category: exp.category.trim(), vendor_name: exp.vendor_name.trim(), amount: amt, remarks: exp.remarks.trim(), order_number: d.order_number, lr_number: d.lr_number || "" });
      setExp({ category: "", vendor_name: "", amount: "", remarks: "" }); setExpKey((k) => k + 1);
      toast.success("Misc. expense added"); await load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const delPayment = async (id) => { try { const { data } = await api.delete(`/leads/${leadId}/vendor-payments/${id}`); setD(data); onChanged?.(); } catch (e) { toast.error(apiErr(e)); } };
  const delExpense = async (id) => { try { await api.delete(`/leads/${leadId}/expenses/${id}`); await load(); onChanged?.(); } catch (e) { toast.error(apiErr(e)); } };

  const blob = async (url, name) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const s = d?.summary || { order_value: 0, paid: 0, pending: 0, misc: 0 };
  const challanFor = (p) => {
    if (!d?.challan_no) return "";
    const t = (d.trucks || []).find((x) => x.lr_id === p.lr_id);
    return t?.suffix ? `${d.challan_no}-${t.suffix}` : d.challan_no;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-hidden flex flex-col" data-testid="vp-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {d ? <>Vendor Payments · <span className="font-mono-num text-primary">{d.order_number}</span></> : "Loading…"}
          </DialogTitle>
        </DialogHeader>

        {!d ? <div className="p-8 text-muted-foreground">Loading…</div> : (
          <div className="overflow-y-auto flex-1 space-y-6 pr-1">
            <div className="text-sm text-muted-foreground">Paid to <b className="text-foreground">{d.vendor_name}</b></div>

            {/* Per-truck ledger */}
            {(d.trucks || []).length > 0 && (
              <section className="border border-border" data-testid="vp-trucks">
                <div className="bg-muted px-3 py-2 text-xs font-bold uppercase tracking-wide flex items-center gap-2"><Truck className="h-4 w-4" /> Per-Truck Ledger</div>
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <tr><th className="px-3 py-1.5 text-left">LR</th><th className="px-3 text-left">Vendor</th><th className="px-3 text-right">Cost</th><th className="px-3 text-right">Paid</th><th className="px-3 text-right">Pending</th><th className="px-3 text-center">Status</th></tr>
                  </thead>
                  <tbody>
                    {d.trucks.map((t) => (
                      <tr key={t.lr_id} className="border-b border-border last:border-0" data-testid={`vp-truck-${t.suffix}`}>
                        <td className="px-3 py-1.5 font-mono-num text-primary">{t.lr_number}<div className="text-[10px] text-muted-foreground font-normal">{(t.goods_type || "").replace("Transportation of ", "")}</div></td>
                        <td className="px-3">{t.vendor_name || "—"}</td>
                        <td className="px-3 text-right font-mono-num">{money(t.vendor_cost)}</td>
                        <td className="px-3 text-right font-mono-num text-emerald-500">{money(t.paid)}</td>
                        <td className={`px-3 text-right font-mono-num font-bold ${t.pending > 0 ? "text-primary" : "text-emerald-500"}`}>{money(t.pending)}</td>
                        <td className="px-3 text-center"><PayStatusBadge status={t.pay_status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <SummaryCard icon={Wallet} label="Order Value" value={s.order_value} />
              <SummaryCard icon={HandCoins} label="Paid" value={s.paid} accent="text-emerald-500" />
              <SummaryCard icon={TrendingDown} label="Pending" value={s.pending} accent="text-primary" />
              <SummaryCard icon={Receipt} label="Misc. Expenses" value={s.misc} />
            </div>

            {/* Add Payment */}
            <section className="border border-primary/40 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><Plus className="h-4 w-4" /> Add Payment</div>
              <div className="grid sm:grid-cols-4 gap-2 items-end">
                <div className="space-y-1"><Label className="text-xs">Amount (₹)</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="vp-pay-amount" /></div>
                <div className="space-y-1"><Label className="text-xs">Truck / LR</Label>
                  <Select value={pay.lr_id || "__order__"} onValueChange={(v) => setPay({ ...pay, lr_id: v === "__order__" ? "" : v })}>
                    <SelectTrigger className="rounded-none h-9 bg-card" data-testid="vp-pay-lr"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__order__">Whole order</SelectItem>
                      {(d.trucks || []).map((t) => <SelectItem key={t.lr_id} value={t.lr_id}>{t.lr_number}{t.vendor_name ? ` · ${t.vendor_name}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Mode of Payment</Label>
                  <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                    <SelectTrigger className="rounded-none h-9 bg-card" data-testid="vp-pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" className="rounded-none bg-card h-9" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="vp-pay-date" /></div>
                <Button className="rounded-none h-9 font-bold uppercase text-xs sm:col-span-4" onClick={addPayment} disabled={busy} data-testid="vp-pay-save">Save Payment</Button>
              </div>
              {pay.mode === "Other" && <Input className="rounded-none bg-card h-9" placeholder="Specify mode…" value={pay.mode_other} onChange={(e) => setPay({ ...pay, mode_other: e.target.value })} data-testid="vp-pay-mode-other" />}
              <Input className="rounded-none bg-card h-9" placeholder="Note / reference (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="vp-pay-note" />
            </section>

            {/* Payments list */}
            <section>
              <div className="text-xs font-bold uppercase tracking-wide mb-2">Payments ({d.payments.length})</div>
              <div className="border border-border divide-y divide-border">
                {d.payments.length === 0 && <div className="p-3 text-sm text-muted-foreground">No payments recorded yet.</div>}
                {d.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 px-3 text-sm" data-testid={`vp-payment-${p.id}`}>
                    <span className="font-mono-num text-[11px] text-muted-foreground w-24 shrink-0">{p.serial}</span>
                    <span className="font-mono-num font-bold w-24">{money(p.amount)}</span>
                    {p.lr_number ? <span className="text-[10px] px-1.5 py-0.5 border border-primary/40 text-primary font-mono-num" data-testid={`vp-payment-lr-${p.id}`}>{p.lr_number}</span> : <span className="text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground">Order</span>}
                    <span className="text-[10px] px-1.5 py-0.5 border border-border bg-muted font-mono-num" title="Challan No. from the Freight Payment Memo (read-only)" data-testid={`vp-payment-challan-${p.id}`}>Challan: {challanFor(p) || "Not generated"}</span>
                    <span className="text-xs px-2 py-0.5 border border-border bg-muted">{p.mode}</span>
                    <span className="text-xs text-muted-foreground flex-1 truncate">{p.date || fmtDate(p.created_at)}{p.note ? ` · ${p.note}` : ""}</span>
                    <Button size="sm" variant="outline" className="rounded-none h-8 gap-1 text-xs" onClick={() => blob(`/leads/${leadId}/vendor-payments/${p.id}/voucher`, `${p.serial.replace(/\//g, "-")}-voucher.pdf`)} data-testid={`vp-voucher-${p.id}`}><Download className="h-3.5 w-3.5" /> Voucher</Button>
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => delPayment(p.id)} data-testid={`vp-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </section>

            {/* Add Misc Expense */}
            <section className="border border-border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide"><Receipt className="h-4 w-4" /> Add Misc. Expense</div>
              <div className="grid sm:grid-cols-5 gap-2 items-end">
                <div className="space-y-1"><Label className="text-xs">Category</Label><ExpenseCategoryField key={expKey} value={exp.category} onChange={(v) => setExp((x) => ({ ...x, category: v }))} testid="vp-exp-category" /></div>
                <div className="space-y-1"><Label className="text-xs">Vendor Name</Label><Input className="rounded-none bg-card h-9" placeholder="optional" value={exp.vendor_name} onChange={(e) => setExp({ ...exp, vendor_name: e.target.value })} data-testid="vp-exp-vendor" /></div>
                <div className="space-y-1"><Label className="text-xs">Amount (₹)</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} data-testid="vp-exp-amount" /></div>
                <div className="space-y-1 sm:col-span-1"><Label className="text-xs">Remarks</Label><Input className="rounded-none bg-card h-9" placeholder="optional" value={exp.remarks} onChange={(e) => setExp({ ...exp, remarks: e.target.value })} data-testid="vp-exp-remarks" /></div>
                <Button className="rounded-none h-9 font-bold uppercase text-xs" onClick={addExpense} disabled={busy} data-testid="vp-exp-save">Add Expense</Button>
              </div>
              <div className="border border-border divide-y divide-border">
                {d.expenses.length === 0 && <div className="p-3 text-sm text-muted-foreground">No misc. expenses.</div>}
                {d.expenses.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-2 px-3 text-sm" data-testid={`vp-expense-${e.id}`}>
                    <span className="font-mono-num text-[11px] text-muted-foreground w-24 shrink-0">{e.serial}</span>
                    <span className="w-28 truncate">{e.category}</span>
                    <span className="w-28 truncate text-xs" data-testid={`vp-exp-vendor-${e.id}`}>{e.vendor_name || "—"}</span>
                    <span className="font-mono-num font-bold w-24">{money(e.amount)}</span>
                    <span className="text-xs text-muted-foreground flex-1 truncate">{e.remarks}</span>
                    <Button size="sm" variant="outline" className="rounded-none h-8 gap-1 text-xs" onClick={() => blob(`/expenses/${e.id}/pdf`, `${e.serial.replace(/\//g, "-")}-expense.pdf`)} data-testid={`vp-exp-voucher-${e.id}`}><Download className="h-3.5 w-3.5" /> Voucher</Button>
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => delExpense(e.id)} data-testid={`vp-exp-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
